import { useState, useEffect } from "react";
import {
  HeartIcon,
  ShareIcon,
  PhotoIcon,
  TagIcon,
  SwatchIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import EditableField from "./EditableField";

interface OutfitItem {
  id: string;
  style?: string;
  category?: {
    name: string;
  };
  colors?: string[];
  tags?: string[];
  imageUrl?: string;
  description?: string | null;
}

interface ItemDetailsProps {
  outfitItem: OutfitItem;
  isFavorited: boolean;
  setIsFavorited: (favorited: boolean) => void;
  editingField: string | null;
  onStartEditing: (field: string) => void;
  onCancelEditing: () => void;
  onSaveField: (field: string) => void;
  onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, action: () => void) => void;
  // Editing state
  editedStyle: string;
  setEditedStyle: (style: string) => void;
  editedCategory: string;
  setEditedCategory: (category: string) => void;
  editedDescription: string;
  setEditedDescription: (description: string) => void;
  editedColors: string[];
  setEditedColors: (colors: string[]) => void;
  editedTags: string[];
  setEditedTags: (tags: string[]) => void;
  newColor: string;
  setNewColor: (color: string) => void;
  newTag: string;
  setNewTag: (tag: string) => void;
  onAddColor: () => void;
  onRemoveColor: (index: number) => void;
  onAddTag: () => void;
  onRemoveTag: (index: number) => void;
}

export default function ItemDetails({
  outfitItem,
  isFavorited,
  setIsFavorited,
  editingField,
  onStartEditing,
  onCancelEditing,
  onSaveField,
  onInputKeyDown,
  editedStyle,
  setEditedStyle,
  editedCategory,
  setEditedCategory,
  editedDescription,
  setEditedDescription,
  editedColors,
  setEditedColors,
  editedTags,
  setEditedTags,
  newColor,
  setNewColor,
  newTag,
  setNewTag,
  onAddColor,
  onRemoveColor,
  onAddTag,
  onRemoveTag,
}: ItemDetailsProps) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Item Image */}
      <div className="aspect-square bg-gray-100 dark:bg-gray-700 flex items-center justify-center relative">
        {outfitItem?.imageUrl ? (
          <img
            src={outfitItem.imageUrl}
            alt={outfitItem.style || "Wardrobe item"}
            className="w-full h-full object-cover"
          />
        ) : (
          <PhotoIcon className="h-24 w-24 text-gray-400" />
        )}
        
        {/* Action buttons overlay */}
        <div className="absolute top-4 right-4 flex space-x-2">
          <button
            onClick={() => setIsFavorited(!isFavorited)}
            className="p-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full hover:scale-110 transition-transform duration-200"
          >
            {isFavorited ? (
              <HeartIconSolid className="h-5 w-5 text-red-500" />
            ) : (
              <HeartIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            )}
          </button>
          <button className="p-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full hover:scale-110 transition-transform duration-200">
            <ShareIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Item Details */}
      <div className="p-6 space-y-6">
        {/* Style */}
        <EditableField
          fieldName="Style"
          value={editedStyle}
          isEditing={editingField === "style"}
          onStartEdit={() => onStartEditing("style")}
          onSave={() => onSaveField("style")}
          onCancel={onCancelEditing}
          onChange={(value) => setEditedStyle(value as string)}
          onKeyDown={(e) => onInputKeyDown(e, () => onSaveField("style"))}
          placeholder="Enter item style (e.g., Casual T-Shirt, Formal Blazer)"
        />

        {/* Category */}
        <EditableField
          fieldName="Category"
          value={editedCategory}
          isEditing={editingField === "category"}
          onStartEdit={() => onStartEditing("category")}
          onSave={() => onSaveField("category")}
          onCancel={onCancelEditing}
          onChange={(value) => setEditedCategory(value as string)}
          onKeyDown={(e) => onInputKeyDown(e, () => onSaveField("category"))}
          placeholder="Enter category (e.g., Tops, Bottoms, Shoes)"
        />

        {/* Colors */}
        <div className="flex items-start space-x-2">
          <SwatchIcon className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
          <div className="flex-1">
            <EditableField
              fieldName="Colors"
              value={editedColors}
              isEditing={editingField === "colors"}
              onStartEdit={() => onStartEditing("colors")}
              onSave={() => onSaveField("colors")}
              onCancel={onCancelEditing}
              onChange={() => {}} // Not used for array type
              onArrayChange={setEditedColors}
              onKeyDown={(e) => onInputKeyDown(e, onAddColor)}
              placeholder="Add a color"
              type="array"
              newItemValue={newColor}
              onNewItemChange={setNewColor}
              onAddItem={onAddColor}
              onRemoveItem={onRemoveColor}
            />
          </div>
        </div>

        {/* Tags */}
        <div className="flex items-start space-x-2">
          <TagIcon className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
          <div className="flex-1">
            <EditableField
              fieldName="Tags"
              value={editedTags}
              isEditing={editingField === "tags"}
              onStartEdit={() => onStartEditing("tags")}
              onSave={() => onSaveField("tags")}
              onCancel={onCancelEditing}
              onChange={() => {}} // Not used for array type
              onArrayChange={setEditedTags}
              onKeyDown={(e) => onInputKeyDown(e, onAddTag)}
              placeholder="Add a tag"
              type="array"
              newItemValue={newTag}
              onNewItemChange={setNewTag}
              onAddItem={onAddTag}
              onRemoveItem={onRemoveTag}
            />
          </div>
        </div>

        {/* Description */}
        <EditableField
          fieldName="Description"
          value={editedDescription}
          isEditing={editingField === "description"}
          onStartEdit={() => onStartEditing("description")}
          onSave={() => onSaveField("description")}
          onCancel={onCancelEditing}
          onChange={(value) => setEditedDescription(value as string)}
          onKeyDown={(e) => onInputKeyDown(e, () => onSaveField("description"))}
          placeholder="Add a description for this item"
        />
      </div>
    </div>
  );
}
